import { AlertTriangle } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "~/components/ui/alert";
import { useFetcher } from "~/hooks/useFetcher";
import {
  PermissionWarningCard,
  type PermissionWarningRow,
} from "~/pages/manifold-nextgen/components/PermissionWarningCard";
import { type ProjectMyResourceAccess, projectMyResourceAccessPath } from "~/services/projects";

interface ProjectMyResourceAccessWarningProps {
  projectId: string;
  // False until the project is known to have something attached, so an empty
  // project doesn't trigger the backend's per-resource fan-out for nothing.
  enabled: boolean;
}

// Warns the caller that they can't use some of the project's datasets or
// agents, without naming them — the caller may not be allowed to see them.
export const ProjectMyResourceAccessWarning = ({
  projectId,
  enabled,
}: ProjectMyResourceAccessWarningProps) => {
  // Grants rarely change mid-session, and the attach/detach flows revalidate
  // this key themselves, so a window focus needn't redo the whole check.
  const { data, error } = useFetcher<ProjectMyResourceAccess>(
    enabled ? projectMyResourceAccessPath(projectId) : null,
    { revalidateOnFocus: false }
  );
  if (error) {
    // Silence here would read as "nothing missing", which is the one thing
    // this banner exists to rule out.
    return (
      <Alert variant="warning">
        <AlertTriangle aria-hidden="true" />
        <AlertTitle className="font-semibold">
          Couldn&apos;t check your access to this project&apos;s resources
        </AlertTitle>
        <AlertDescription>
          <p>
            We couldn&apos;t check whether you can use all of this project&apos;s datasets and
            agents. If one won&apos;t open for you, reach out to its owner to request access.
          </p>
        </AlertDescription>
      </Alert>
    );
  }
  if (!data) {
    return null;
  }

  const rows: PermissionWarningRow[] = [];
  if (data.missing_dataset_access) {
    rows.push({
      key: "datasets",
      kind: "dataset",
      subject: "You",
      text: "don't have access to one or more datasets on this project. Reach out to the datasets' owners to request access.",
    });
  }
  if (data.missing_agent_access || data.missing_toolkit_scopes) {
    rows.push({
      key: "agents",
      kind: "agent",
      subject: "You",
      text: "don't have access to one or more agents on this project. Reach out to the agents' owners to request access.",
    });
  }
  if (rows.length === 0) {
    return null;
  }

  return (
    <PermissionWarningCard
      rows={rows}
      footer="Until access is granted, you won't be able to reproduce others' work on this project."
    />
  );
};
